import type React from 'react';
import { Link, useNavigate } from 'react-router';
import styled from '@emotion/styled';
import {
  Phone, MessageSquare, Megaphone, Users,
} from 'lucide-react';
import { Card, CardGrid, PageHeader, SectionLabel, Muted, Badge, Skeleton } from '../styled-components';
import { Sparkline } from '../components';
import { useFetchData, useCountUp, useLocalStorage } from '../hooks';

function formatPhone(number: string): string {
  if (!number) return '—';
  const digits = number.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return number;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface KpiValue {
  value: number;
  trend: { value: number | null; direction: 'up' | 'down' | null };
}

interface HomeSummary {
  window: string;
  kpis: Record<string, KpiValue>;
  sparkline_data: { date: string; count: number }[];
  recent_conversations: Array<{
    id: string; call_id: string; ts: string | null;
    direction: string; from_number: string; to_number: string; duration_sec: number | null;
  }>;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const WindowToggle = styled.div({
  display: 'flex',
  gap: 2,
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 6,
  padding: 2,
});

const WindowBtn = styled.button<{ active?: boolean }>(({ active }) => ({
  background: active ? 'var(--accent-cyan-glow)' : 'none',
  border: 'none',
  borderRadius: 4,
  padding: '4px 12px',
  fontSize: 13,
  fontWeight: active ? 600 : 400,
  color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
  cursor: 'pointer',
  transition: 'all 150ms',
}));

const KpiCard = styled(Card)<{ delay?: number }>(({ delay = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  animation: `cardIn 200ms ease ${delay}ms both`,
}));

const KpiLabel = styled.div({
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-secondary)',
});

const KpiValue = styled.div({
  fontSize: 32,
  fontWeight: 700,
  color: 'var(--text-primary)',
  lineHeight: 1.1,
  fontVariantNumeric: 'tabular-nums',
});

const KpiTrend = styled.span<{ direction?: string }>(({ direction }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: direction === 'up' ? 'var(--accent-green)' : direction === 'down' ? 'var(--accent-red)' : 'var(--text-muted)',
  marginLeft: 8,
}));

const KpiFooter = styled.div({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginTop: 4,
});

// ---------------------------------------------------------------------------
// KPI card with count-up animation
// ---------------------------------------------------------------------------

const KPI_META: Record<string, { label: string; icon: React.ReactNode }> = {
  calls:     { label: 'CALLS',        icon: <MessageSquare size={14} /> },
  minutes:   { label: 'MINUTES',      icon: <Phone size={14} /> },
  campaigns: { label: 'CAMPAIGNS',    icon: <Megaphone size={14} /> },
  numbers:   { label: 'NUMBERS',      icon: <Phone size={14} /> },
  team:      { label: 'TEAM MEMBERS', icon: <Users size={14} /> },
};

function KpiCardComponent({
  kpiKey, kpi, sparkline, window: win, delay,
}: {
  kpiKey: string;
  kpi: KpiValue;
  sparkline: number[];
  window: string;
  delay: number;
}) {
  const displayValue = useCountUp(kpi.value, 600);
  const meta = KPI_META[kpiKey];
  if (!meta) return null;

  const trend = kpi.trend;
  const trendStr = trend.value == null ? '—'
    : `${trend.direction === 'up' ? '↑' : '↓'} ${trend.value}%`;

  return (
    <KpiCard delay={delay}>
      <KpiLabel>{meta.label}</KpiLabel>
      <div>
        <KpiValue>
          {displayValue.toLocaleString()}
          {trend.value != null && (
            <KpiTrend direction={trend.direction ?? undefined}>{trendStr}</KpiTrend>
          )}
        </KpiValue>
      </div>
      <KpiFooter>
        <Muted>last {win}</Muted>
        <Sparkline data={sparkline} width={80} height={28} />
      </KpiFooter>
    </KpiCard>
  );
}

// ---------------------------------------------------------------------------
// Recent conversations table
// ---------------------------------------------------------------------------

function formatDuration(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTs(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const RecentTable = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
  '& th': {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
    color: 'var(--text-secondary)', padding: '10px 12px 10px', textAlign: 'left',
    borderBottom: '1px solid var(--bg-border)',
  },
  '& td': { padding: '0 12px', height: 44, fontSize: 13, color: 'var(--text-primary)' },
  '& tr:hover td': { background: 'var(--bg-surface-hover)' },
});

// ---------------------------------------------------------------------------
// Health alert
// ---------------------------------------------------------------------------

const HealthCard = styled(Card)({
  background: 'var(--accent-amber-glow)',
  borderColor: 'rgba(245,149,0,0.2)',
  color: 'var(--accent-amber)',
  fontSize: 14,
});

// ---------------------------------------------------------------------------
// Getting started
// ---------------------------------------------------------------------------

const GsCard = styled.div({
  background: 'var(--bg-surface)',
  border: '1px solid var(--bg-border)',
  borderRadius: 14,
  overflow: 'hidden',
  animation: 'cardIn 400ms cubic-bezier(0.22, 1, 0.36, 1) both',
  boxShadow: '0 1px 3px rgba(0,0,0,0.4), 0 0 0 1px rgba(0,0,0,0.2)',
});

const GsHeader = styled.div({
  padding: '32px 36px 30px',
  background: 'linear-gradient(135deg, var(--bg-surface) 0%, rgba(0,200,117,0.05) 60%, rgba(0,200,117,0.02) 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -60, right: -60,
    width: 200, height: 200,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,200,117,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
});

function GettingStartedCard() {
  return (
    <GsCard>
      <GsHeader>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent-cyan)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>
            Quick start
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            New to Guava?
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
            Follow the quickstart guide to build and ship your first voice agent.
          </div>
          <a href="https://goguava.ai/docs" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: 'var(--text-primary)',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.04em', whiteSpace: 'nowrap',
            border: '1px solid rgba(0,200,117,0.3)',
            borderRadius: 6, padding: '6px 12px',
            textDecoration: 'none',
          }}>
            Quickstart Guide →
          </a>
        </div>
      </GsHeader>
    </GsCard>
  );
}

// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------

// KPIs that have meaningful time-series sparklines (call volume over time)
const SPARKLINE_KPIS = new Set(['calls', 'minutes']);

export function HomePage() {
  const navigate = useNavigate();
  const [timeWindow, setTimeWindow] = useLocalStorage<string>('home-window', '30d');
  const { data, loading } = useFetchData<HomeSummary | null>(
    `/dashboard/api/home/summary?window=${timeWindow}`,
    null,
  );

  const sparklineArr = data?.sparkline_data?.map((d) => d.count) ?? [];

  const visibleKpis = data
    ? Object.entries(data.kpis).filter(([k, v]) => v.value > 0 && k !== 'team')
    : [];

  const showGettingStarted = !loading && (data?.kpis?.calls?.value ?? 0) === 0;

  return (
    <div>
      <PageHeader>
        <WindowToggle>
          {['7d', '30d', '90d'].map((w) => (
            <WindowBtn key={w} active={timeWindow === w} onClick={() => setTimeWindow(w)}>{w}</WindowBtn>
          ))}
        </WindowToggle>
      </PageHeader>

      {loading ? (
        <CardGrid cols={4}>
          {(['s0', 's1', 's2', 's3'] as const).map((key) => (
            <Card key={key} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Skeleton height={11} width="40%" />
              <Skeleton height={32} width="60%" />
              <Skeleton height={28} width="80px" />
            </Card>
          ))}
        </CardGrid>
      ) : showGettingStarted ? (
        <GettingStartedCard />
      ) : (
        <CardGrid cols={Math.min(visibleKpis.length, 4)}>
          {visibleKpis.map(([key, kpi], i) => (
            <KpiCardComponent
              key={key}
              kpiKey={key}
              kpi={kpi}
              sparkline={SPARKLINE_KPIS.has(key) ? sparklineArr : []}
              window={timeWindow}
              delay={i * 40}
            />
          ))}
        </CardGrid>
      )}

      {/* Developer health alert */}
      {data && (data.kpis.missed_callbacks?.value > 0 || data.kpis.avg_callback_response?.value > 500) && (
        <HealthCard style={{ marginTop: 24 }}>
          {data.kpis.missed_callbacks?.value > 0
            ? `${data.kpis.missed_callbacks.value} callbacks went unanswered in the last ${timeWindow}.`
            : `Avg callback response time is ${data.kpis.avg_callback_response?.value}ms — consider optimizing your on_question / on_intent handlers.`}
        </HealthCard>
      )}

      {/* Recent conversations */}
      {data && data.recent_conversations.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <SectionLabel>Recent Conversations</SectionLabel>
            <Link to="/conversations" style={{ fontSize: 13 }}>View all →</Link>
          </div>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <RecentTable>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Direction</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_conversations.map((c) => (
                  <tr key={c.call_id} onClick={() => navigate(`/conversations/${c.call_id}`)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontSize: 13 }}>{formatTs(c.ts)}</td>
                    <td>
                      <Badge variant={c.direction}>{c.direction}</Badge>
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{formatPhone(c.from_number)}</td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{formatPhone(c.to_number)}</td>
                    <td>{formatDuration(c.duration_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </RecentTable>
          </Card>
        </div>
      )}
    </div>
  );
}
